import React, { useState, useEffect, useRef } from 'react';

const CosmicMode = ({ audioContext, isSoundEnabled }) => {
  // --- STATE ---
  const [stars, setStars] = useState([])
  const [showHint, setShowHint] = useState(true)
  const [hintClicked, setHintClicked] = useState(false)
  const [showAdvancedHint, setShowAdvancedHint] = useState(false)
  const [advancedHintShown, setAdvancedHintShown] = useState(false)
  const [rightClickCount, setRightClickCount] = useState(0)
  const [lastRightClickTime, setLastRightClickTime] = useState(0)
  const [isRightMouseHeld, setIsRightMouseHeld] = useState(false)
  const [explosions, setExplosions] = useState([])
  const [lastTapTime, setLastTapTime] = useState(0)
  const [tapCount, setTapCount] = useState(0)
  const [bothButtonsPressed, setBothButtonsPressed] = useState(false)
  const [fallingStars, setFallingStars] = useState([])
  const [fallingStarsInterval, setFallingStarsInterval] = useState(null)
  const [lastStarSoundTime, setLastStarSoundTime] = useState(0)
  // Removed internal isSoundEnabled state (now a prop)
  
  // Новые состояния для доп. эффектов
  const [nebulaParticles, setNebulaParticles] = useState([])
  const [isLeftMouseHeld, setIsLeftMouseHeld] = useState(false)
  const [isMiddleMouseHeld, setIsMiddleMouseHeld] = useState(false)
  const [blackHolePos, setBlackHolePos] = useState({ x: 0, y: 0 })
  const [blackHoleMatter, setBlackHoleMatter] = useState([])
  const [isBlackHoleClosing, setIsBlackHoleClosing] = useState(false)
  const [blackHoleOscillators, setBlackHoleOscillators] = useState(null)
  const [nebulaSoundNode, setNebulaSoundNode] = useState(null) // State kept for completeness, even if logic unused
  const [showControlsInfo, setShowControlsInfo] = useState(false)

  // Removed useEffect for localStorage (handled in parent) - resuming context handled by effect in App.js or prop changes

  // --- AUDIO LOGIC ---
  
  // Звук созвездия (Связи) - мягкий, теплый колокольчик без писка
  const playConstellationChime = () => {
    if (!audioContext || !isSoundEnabled) return
    
    // Выбираем ноту из пентатоники (пониже, чтобы не пищало)
    const notes = [261.63, 293.66, 329.63, 392.00, 440.00] // C4 - A4
    const freq = notes[Math.floor(Math.random() * notes.length)]
    
    const osc = audioContext.createOscillator()
    const gain = audioContext.createGain()
    const filter = audioContext.createBiquadFilter() // Фильтр чтобы срезать "стекло"
    
    osc.connect(filter)
    filter.connect(gain)
    gain.connect(audioContext.destination)
    
    // Lowpass фильтр срезает все что выше 800Hz, оставляя мягкий тон
    filter.type = 'lowpass'
    filter.frequency.value = 800 
    
    osc.type = 'sine'
    osc.frequency.value = freq
    
    gain.gain.setValueAtTime(0, audioContext.currentTime)
    gain.gain.linearRampToValueAtTime(0.04, audioContext.currentTime + 0.05) // Очень тихая атака
    gain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 1.5) // Долгий хвост
    
    osc.start(audioContext.currentTime)
    osc.stop(audioContext.currentTime + 1.5)
    
    // Легкая вибрация-отклик
    const vib = audioContext.createOscillator()
    const vibGain = audioContext.createGain()
    vib.connect(vibGain)
    vibGain.connect(audioContext.destination)
    vib.frequency.value = 40 // Мягкий гул
    vibGain.gain.setValueAtTime(0.05, audioContext.currentTime)
    vibGain.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.2)
    vib.start(audioContext.currentTime)
    vib.stop(audioContext.currentTime + 0.2)
  }

  // Звук Черной дыры - Низкий гул + Мощная вибрация
  const startBlackHoleSound = () => {
    if (!audioContext || !isSoundEnabled) return

    // 1. Shepard Tone Drone (Бесконечное падение)
    const osc1 = audioContext.createOscillator()
    const osc2 = audioContext.createOscillator()
    const gain = audioContext.createGain()
    
    osc1.frequency.value = 100
    osc2.frequency.value = 102 // Биения (Binaural beats effect)
    
    osc1.type = 'sawtooth' // Агрессивнее
    osc2.type = 'sine'

    // Фильтр, который открывается
    const filter = audioContext.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 100
    filter.frequency.linearRampToValueAtTime(800, audioContext.currentTime + 5) // Нарастание напряжения

    // 2. Саб-бас вибрация (Физическая)
    const sub = audioContext.createOscillator()
    const subGain = audioContext.createGain()
    sub.frequency.value = 45 // Глубокий бас
    sub.type = 'sine'
    
    // LFO для вибрации ("Воббл")
    const lfo = audioContext.createOscillator()
    lfo.frequency.value = 8 // Скорость вибрации
    lfo.frequency.linearRampToValueAtTime(20, audioContext.currentTime + 5) // Ускорение
    const lfoGain = audioContext.createGain()
    lfoGain.gain.value = 20
    
    lfo.connect(lfoGain)
    lfoGain.connect(sub.frequency) // FM синтез для баса

    osc1.connect(filter)
    osc2.connect(filter)
    filter.connect(gain)
    gain.connect(audioContext.destination)
    
    sub.connect(subGain)
    subGain.connect(audioContext.destination)
    
    gain.gain.value = 0
    gain.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 1)
    
    subGain.gain.value = 0
    subGain.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.5)

    osc1.start()
    osc2.start()
    sub.start()
    lfo.start()
    
    setBlackHoleOscillators({ osc1, osc2, sub, lfo, gain, subGain })
  }

  const stopBlackHoleSound = () => {
    if (!blackHoleOscillators) return
    const { osc1, osc2, sub, lfo, gain, subGain } = blackHoleOscillators
    
    try {
      const now = audioContext.currentTime
      gain.gain.linearRampToValueAtTime(0, now + 0.5)
      subGain.gain.linearRampToValueAtTime(0, now + 0.5)
      
      // Звук "схлопывания" в конце
      const collapse = audioContext.createOscillator()
      const cGain = audioContext.createGain()
      collapse.frequency.value = 50
      collapse.frequency.exponentialRampToValueAtTime(0.01, now + 0.5)
      cGain.gain.value = 0.2
      cGain.gain.exponentialRampToValueAtTime(0.001, now + 0.5)
      collapse.connect(cGain)
      cGain.connect(audioContext.destination)
      collapse.start()
      collapse.stop(now + 0.5)
      
      setTimeout(() => {
        osc1.stop()
        osc2.stop()
        sub.stop()
        lfo.stop()
      }, 500)
    } catch(e) {}
    setBlackHoleOscillators(null)
  }

  // Звук обычной звезды - короткая мягкая волна как при двойном клике, но тише и быстрее
  const playStarSound = () => {
    if (!audioContext || !isSoundEnabled) return
    const now = Date.now()
    if (now - lastStarSoundTime < 200) return // Еще реже
    setLastStarSoundTime(now)
    
    // Короткая мягкая волна на ноте C4 (261.63 Hz) - одна октава
    const wave = audioContext.createOscillator()
    const waveGain = audioContext.createGain()
    const waveFilter = audioContext.createBiquadFilter()
    
    wave.connect(waveFilter)
    waveFilter.connect(waveGain)
    waveGain.connect(audioContext.destination)
    
    waveFilter.type = 'lowpass'
    waveFilter.frequency.setValueAtTime(400, audioContext.currentTime)
    waveFilter.frequency.linearRampToValueAtTime(200, audioContext.currentTime + 0.6)
    waveFilter.Q.value = 1.5
    
    wave.frequency.setValueAtTime(261.63, audioContext.currentTime) // C4
    
    // Очень мягкое и быстрое нарастание/затухание
    waveGain.gain.setValueAtTime(0, audioContext.currentTime)
    waveGain.gain.linearRampToValueAtTime(0.04, audioContext.currentTime + 0.08)
    waveGain.gain.linearRampToValueAtTime(0.03, audioContext.currentTime + 0.2)
    waveGain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + 0.8)
    
    wave.type = 'sine'
    wave.start(audioContext.currentTime)
    wave.stop(audioContext.currentTime + 0.8)
    
    // Легкая гармоника C5 для нежности
    const harmony = audioContext.createOscillator()
    const harmonyGain = audioContext.createGain()
    const harmonyFilter = audioContext.createBiquadFilter()
    
    harmony.connect(harmonyFilter)
    harmonyFilter.connect(harmonyGain)
    harmonyGain.connect(audioContext.destination)
    
    harmonyFilter.type = 'bandpass'
    harmonyFilter.frequency.setValueAtTime(600, audioContext.currentTime)
    harmonyFilter.Q.value = 2
    
    harmony.frequency.setValueAtTime(523.25, audioContext.currentTime) // C5
    
    harmonyGain.gain.setValueAtTime(0, audioContext.currentTime + 0.05)
    harmonyGain.gain.linearRampToValueAtTime(0.015, audioContext.currentTime + 0.15)
    harmonyGain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + 0.6)
    
    harmony.type = 'sine'
    harmony.start(audioContext.currentTime + 0.05)
    harmony.stop(audioContext.currentTime + 0.6)
    
    // Вибрационный слой для физического ощущения через колонки
    const vibration = audioContext.createOscillator()
    const vibGain = audioContext.createGain()
    const vibFilter = audioContext.createBiquadFilter()
    
    vibration.connect(vibFilter)
    vibFilter.connect(vibGain)
    vibGain.connect(audioContext.destination)
    
    vibFilter.type = 'lowpass'
    vibFilter.frequency.setValueAtTime(60, audioContext.currentTime)
    vibFilter.Q.value = 1
    
    vibration.frequency.setValueAtTime(32.70, audioContext.currentTime) // C1 - очень низкая частота для вибраций
    
    vibGain.gain.setValueAtTime(0, audioContext.currentTime)
    vibGain.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.05)
    vibGain.gain.linearRampToValueAtTime(0.05, audioContext.currentTime + 0.15)
    vibGain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + 0.6)
    
    vibration.type = 'sine'
    vibration.start(audioContext.currentTime)
    vibration.stop(audioContext.currentTime + 0.6)
  }

  // Звук взрыва - мягкая расходящаяся волна с нотой ДО (как волны на воде)
  const playExplosionSound = () => {
    if (!audioContext || !isSoundEnabled) return
    
    // Основная волна на ноте C3 (130.81 Hz) - мягкое расширение
    const mainWave = audioContext.createOscillator()
    const mainGain = audioContext.createGain()
    const mainFilter = audioContext.createBiquadFilter()
    
    mainWave.connect(mainFilter)
    mainFilter.connect(mainGain)
    mainGain.connect(audioContext.destination)
    
    mainFilter.type = 'lowpass'
    mainFilter.frequency.setValueAtTime(500, audioContext.currentTime)
    mainFilter.frequency.linearRampToValueAtTime(150, audioContext.currentTime + 3)
    mainFilter.Q.value = 2
    
    mainWave.frequency.setValueAtTime(130.81, audioContext.currentTime) // C3
    
    // Очень плавная волна - медленное нарастание и долгое затухание
    mainGain.gain.setValueAtTime(0, audioContext.currentTime)
    mainGain.gain.linearRampToValueAtTime(0.18, audioContext.currentTime + 0.2)
    mainGain.gain.linearRampToValueAtTime(0.14, audioContext.currentTime + 0.8)
    mainGain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + 4)
    
    mainWave.type = 'sine'
    mainWave.start(audioContext.currentTime)
    mainWave.stop(audioContext.currentTime + 4)
    
    // Октава выше C4 (261.63 Hz) - гармоничное дополнение
    const harmony1 = audioContext.createOscillator()
    const harmony1Gain = audioContext.createGain()
    const harmony1Filter = audioContext.createBiquadFilter()
    
    harmony1.connect(harmony1Filter)
    harmony1Filter.connect(harmony1Gain)
    harmony1Gain.connect(audioContext.destination)
    
    harmony1Filter.type = 'bandpass'
    harmony1Filter.frequency.setValueAtTime(600, audioContext.currentTime)
    harmony1Filter.frequency.linearRampToValueAtTime(300, audioContext.currentTime + 3.5)
    harmony1Filter.Q.value = 1.5
    
    harmony1.frequency.setValueAtTime(261.63, audioContext.currentTime) // C4
    
    harmony1Gain.gain.setValueAtTime(0, audioContext.currentTime)
    harmony1Gain.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.3)
    harmony1Gain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + 3.8)
    
    harmony1.type = 'sine'
    harmony1.start(audioContext.currentTime)
    harmony1.stop(audioContext.currentTime + 3.8)
    
    // C5 (523.25 Hz) - высокая октава для легкости
    const harmony2 = audioContext.createOscillator()
    const harmony2Gain = audioContext.createGain()
    const harmony2Filter = audioContext.createBiquadFilter()
    
    harmony2.connect(harmony2Filter)
    harmony2Filter.connect(harmony2Gain)
    harmony2Gain.connect(audioContext.destination)
    
    harmony2Filter.type = 'bandpass'
    harmony2Filter.frequency.setValueAtTime(1000, audioContext.currentTime)
    harmony2Filter.frequency.linearRampToValueAtTime(600, audioContext.currentTime + 3)
    harmony2Filter.Q.value = 2
    
    harmony2.frequency.setValueAtTime(523.25, audioContext.currentTime) // C5
    
    harmony2Gain.gain.setValueAtTime(0, audioContext.currentTime + 0.1)
    harmony2Gain.gain.linearRampToValueAtTime(0.05, audioContext.currentTime + 0.4)
    harmony2Gain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + 3.5)
    
    harmony2.type = 'sine'
    harmony2.start(audioContext.currentTime + 0.1)
    harmony2.stop(audioContext.currentTime + 3.5)
    
    // Глубокий суб-бас C2 (65.41 Hz) для ощущения глубины
    const subBass = audioContext.createOscillator()
    const subGain = audioContext.createGain()
    const subFilter = audioContext.createBiquadFilter()
    
    subBass.connect(subFilter)
    subFilter.connect(subGain)
    subGain.connect(audioContext.destination)
    
    subFilter.type = 'lowpass'
    subFilter.frequency.setValueAtTime(100, audioContext.currentTime)
    
    subBass.frequency.setValueAtTime(65.41, audioContext.currentTime) // C2
    
    subGain.gain.setValueAtTime(0, audioContext.currentTime)
    subGain.gain.linearRampToValueAtTime(0.12, audioContext.currentTime + 0.15)
    subGain.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 1)
    subGain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + 4.5)
    
    subBass.type = 'sine'
    subBass.start(audioContext.currentTime)
    subBass.stop(audioContext.currentTime + 4.5)
    
    // Мощная вибрация для взрывного эффекта
    const vibration = audioContext.createOscillator()
    const vibGain = audioContext.createGain()
    const vibFilter = audioContext.createBiquadFilter()
    
    vibration.connect(vibFilter)
    vibFilter.connect(vibGain)
    vibGain.connect(audioContext.destination)
    
    vibFilter.type = 'lowpass'
    vibFilter.frequency.setValueAtTime(40, audioContext.currentTime)
    vibFilter.Q.value = 2
    
    vibration.frequency.setValueAtTime(20, audioContext.currentTime) // 20 Hz - сильная вибрация
    
    vibGain.gain.setValueAtTime(0, audioContext.currentTime)
    vibGain.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.1)
    vibGain.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.5)
    vibGain.gain.linearRampToValueAtTime(0.05, audioContext.currentTime + 1.5)
    vibGain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + 3)
    
    vibration.type = 'sine'
    vibration.start(audioContext.currentTime)
    vibration.stop(audioContext.currentTime + 3)
    
    // Дополнительная вибрация на 30 Hz для более комплексного ощущения
    const vibration2 = audioContext.createOscillator()
    const vib2Gain = audioContext.createGain()
    const vib2Filter = audioContext.createBiquadFilter()
    
    vibration2.connect(vib2Filter)
    vib2Filter.connect(vib2Gain)
    vib2Gain.connect(audioContext.destination)
    
    vib2Filter.type = 'lowpass'
    vib2Filter.frequency.setValueAtTime(50, audioContext.currentTime)
    vib2Filter.Q.value = 1.5
    
    vibration2.frequency.setValueAtTime(30, audioContext.currentTime) // 30 Hz
    
    vib2Gain.gain.setValueAtTime(0, audioContext.currentTime + 0.05)
    vib2Gain.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.2)
    vib2Gain.gain.linearRampToValueAtTime(0.07, audioContext.currentTime + 1)
    vib2Gain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + 2.5)
    
    vibration2.type = 'sine'
    vibration2.start(audioContext.currentTime + 0.05)
    vibration2.stop(audioContext.currentTime + 2.5)
  }

  // Звук падающих звезд - нежная медитативная атмосфера для полного расслабления
  const playFallingStarsSound = () => {
    if (!audioContext || !isSoundEnabled) return
    
    // Глубокий нежный drone на ноте C2 (65.41 Hz) - очень тихая базовая частота
    const bassDrone = audioContext.createOscillator()
    const bassGain = audioContext.createGain()
    const bassFilter = audioContext.createBiquadFilter()
    
    bassDrone.connect(bassFilter)
    bassFilter.connect(bassGain)
    bassGain.connect(audioContext.destination)
    
    bassFilter.type = 'lowpass'
    bassFilter.frequency.setValueAtTime(150, audioContext.currentTime)
    bassFilter.Q.value = 0.5
    
    bassDrone.frequency.setValueAtTime(65.41, audioContext.currentTime) // C2
    
    bassGain.gain.setValueAtTime(0, audioContext.currentTime)
    bassGain.gain.linearRampToValueAtTime(0.04, audioContext.currentTime + 3.5)
    bassGain.gain.setValueAtTime(0.04, audioContext.currentTime + 6)
    
    bassDrone.type = 'sine'
    bassDrone.start(audioContext.currentTime)
    
    // Средний нежный drone на ноте G2 (98 Hz) - гармоничная квинта
    const midDrone = audioContext.createOscillator()
    const midGain = audioContext.createGain()
    const midFilter = audioContext.createBiquadFilter()
    
    midDrone.connect(midFilter)
    midFilter.connect(midGain)
    midGain.connect(audioContext.destination)
    
    midFilter.type = 'lowpass'
    midFilter.frequency.setValueAtTime(200, audioContext.currentTime)
    midFilter.Q.value = 0.5
    
    midDrone.frequency.setValueAtTime(98, audioContext.currentTime) // G2
    
    midGain.gain.setValueAtTime(0, audioContext.currentTime)
    midGain.gain.linearRampToValueAtTime(0.035, audioContext.currentTime + 4)
    midGain.gain.setValueAtTime(0.035, audioContext.currentTime + 6)
    
    midDrone.type = 'sine'
    midDrone.start(audioContext.currentTime)
    
    // Верхний мягкий drone на ноте C3 (130.81 Hz) - октава
    const highDrone = audioContext.createOscillator()
    const highGain = audioContext.createGain()
    const highFilter = audioContext.createBiquadFilter()
    
    highDrone.connect(highFilter)
    highFilter.connect(highGain)
    highGain.connect(audioContext.destination)
    
    highFilter.type = 'lowpass'
    highFilter.frequency.setValueAtTime(250, audioContext.currentTime)
    highFilter.Q.value = 0.5
    
    highDrone.frequency.setValueAtTime(130.81, audioContext.currentTime) // C3
    
    highGain.gain.setValueAtTime(0, audioContext.currentTime)
    highGain.gain.linearRampToValueAtTime(0.03, audioContext.currentTime + 4.5)
    highGain.gain.setValueAtTime(0.03, audioContext.currentTime + 6.5)
    
    highDrone.type = 'sine'
    highDrone.start(audioContext.currentTime)
    
    // Очень медленная нежная волна океана - нота E3 (164.81 Hz) с глубокой модуляцией
    const oceanWave = audioContext.createOscillator()
    const waveGain = audioContext.createGain()
    const waveFilter = audioContext.createBiquadFilter()
    
    // LFO для имитации волн океана
    const waveLfo = audioContext.createOscillator()
    const waveLfoGain = audioContext.createGain()
    
    waveLfo.frequency.setValueAtTime(0.12, audioContext.currentTime) // Еще медленнее
    waveLfoGain.gain.setValueAtTime(0.015, audioContext.currentTime) // Еще тоньше
    
    waveLfo.connect(waveLfoGain)
    waveLfoGain.connect(waveGain.gain)
    
    oceanWave.connect(waveFilter)
    waveFilter.connect(waveGain)
    waveGain.connect(audioContext.destination)
    
    waveFilter.type = 'bandpass'
    waveFilter.frequency.setValueAtTime(280, audioContext.currentTime)
    waveFilter.Q.value = 0.8
    
    oceanWave.frequency.setValueAtTime(164.81, audioContext.currentTime) // E3
    
    waveGain.gain.setValueAtTime(0, audioContext.currentTime)
    waveGain.gain.linearRampToValueAtTime(0.025, audioContext.currentTime + 5)
    waveGain.gain.setValueAtTime(0.025, audioContext.currentTime + 7)
    
    oceanWave.type = 'sine'
    waveLfo.type = 'sine'
    
    oceanWave.start(audioContext.currentTime)
    waveLfo.start(audioContext.currentTime)
    
    // Мягкая вибрация для расслабляющего массажного эффекта
    const vibration = audioContext.createOscillator()
    const vibGain = audioContext.createGain()
    const vibFilter = audioContext.createBiquadFilter()
    
    // Медленная модуляция для мягкого массажа
    const vibLfo = audioContext.createOscillator()
    const vibLfoGain = audioContext.createGain()
    
    vibLfo.frequency.setValueAtTime(0.1, audioContext.currentTime) // Очень медленно
    vibLfoGain.gain.setValueAtTime(0.02, audioContext.currentTime) // Мягкая модуляция
    
    vibLfo.connect(vibLfoGain)
    vibLfoGain.connect(vibGain.gain)
    
    vibration.connect(vibFilter)
    vibFilter.connect(vibGain)
    vibGain.connect(audioContext.destination)
    
    vibFilter.type = 'lowpass'
    vibFilter.frequency.setValueAtTime(35, audioContext.currentTime)
    vibFilter.Q.value = 1
    
    vibration.frequency.setValueAtTime(25, audioContext.currentTime) // 25 Hz - мягкая вибрация
    
    vibGain.gain.setValueAtTime(0, audioContext.currentTime)
    vibGain.gain.linearRampToValueAtTime(0.06, audioContext.currentTime + 4)
    vibGain.gain.setValueAtTime(0.06, audioContext.currentTime + 8)
    
    vibration.type = 'sine'
    vibLfo.type = 'sine'
    
    vibration.start(audioContext.currentTime)
    vibLfo.start(audioContext.currentTime)
    
    // Сохраняем осцилляторы для остановки
    window.fallingStarsOscillators = { 
      bassDrone, midDrone, highDrone, oceanWave, waveLfo, vibration, vibLfo,
      bassGain, midGain, highGain, waveGain, vibGain
    }
  }
  
  const stopFallingStarsSound = () => {
    if (!audioContext || !window.fallingStarsOscillators) return
    
    const { bassDrone, midDrone, highDrone, oceanWave, waveLfo, vibration, vibLfo,
            bassGain, midGain, highGain, waveGain, vibGain } = window.fallingStarsOscillators
    
    // Быстрое плавное затухание - 2.5 секунды
    const fadeTime = 2.5
    
    bassGain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + fadeTime)
    midGain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + fadeTime)
    highGain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + fadeTime)
    waveGain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + fadeTime)
    vibGain.gain.linearRampToValueAtTime(0.001, audioContext.currentTime + fadeTime)
    
    // Останавливаем все осцилляторы после затухания
    setTimeout(() => {
      try {
        bassDrone.stop()
        midDrone.stop()
        highDrone.stop()
        oceanWave.stop()
        waveLfo.stop()
        vibration.stop()
        vibLfo.stop()
      } catch (e) {
        // Игнорируем ошибки если уже остановлены
      }
      window.fallingStarsOscillators = null
    }, fadeTime * 1000 + 100)
  }

  // --- LOGIC ---
  const createStar = (x, y, gentle = false, isMagic = false) => {
    const id = Date.now() + Math.random()
    const angle = Math.random() * 360
    const distance = isMagic 
      ? Math.random() * 150 + 100 
      : gentle 
        ? Math.random() * 50 + 30 
        : Math.random() * 80 + 40
    const size = isMagic ? Math.random() * 6 + 4 : Math.random() * 3 + 2
    const points = Math.floor(Math.random() * 3) + 4
    const duration = isMagic 
      ? Math.random() * 2 + 2.5 
      : gentle 
        ? Math.random() * 1 + 2 
        : Math.random() * 0.8 + 1.5
    const rotation = Math.random() * 360
    const star = { id, x, y, angle, distance, size, points, duration, rotation, isMagic, fadeOut: false }
    setStars(prev => [...prev, star])
    
    // Звук для обычных звезд
    if (!isMagic) {
      playStarSound()
    }
    
    // Удаляем звезду сразу после окончания основной анимации (без fadeOut)
    setTimeout(() => {
      setStars(prev => prev.filter(s => s.id !== id))
    }, duration * 1000)
  }

  const createExplosion = (x, y) => {
    const id = Date.now() + Math.random()
    const explosion = { id, x, y }
    setExplosions(prev => [...prev, explosion])
    
    // Звук взрыва
    playExplosionSound()
    
    // Создаём кольцо из звёзд
    for (let i = 0; i < 24; i++) {
    //   const angle = (360 / 24) * i
      setTimeout(() => {
        createStar(x, y, false, true)
      }, i * 20)
    }
    
    setTimeout(() => setExplosions(prev => prev.filter(e => e.id !== id)), 1000)
  }

  const createFallingStar = () => {
    const id = Date.now() + Math.random()
    const x = Math.random() * window.innerWidth
    const size = Math.random() * 4 + 2
    const points = Math.floor(Math.random() * 3) + 4
    const rotation = Math.random() * 360
    const duration = Math.random() * 4 + 5 // 5-9 секунд для плавного падения
    const delay = 0 // убираем задержку появления
    
    const star = { 
      id, 
      x, 
      size, 
      points, 
      rotation, 
      duration,
      delay,
      fadeOut: false,
      startTime: Date.now() // Запоминаем время создания
    }
    
    setFallingStars(prev => [...prev, star])
    
    // Удаляем звезду после окончания анимации
    setTimeout(() => {
      setFallingStars(prev => prev.filter(s => s.id !== id))
    }, (duration + delay) * 1000)
  }

  const startFallingStars = () => {
    playFallingStarsSound()
    const interval = setInterval(() => {
      createFallingStar()
    }, 100) // Создаём новую звезду каждые 100мс
    setFallingStarsInterval(interval)
  }

  const stopFallingStars = () => {
    stopFallingStarsSound()
    if (fallingStarsInterval) {
      clearInterval(fallingStarsInterval)
      setFallingStarsInterval(null)
    }
    // Получаем текущее время для расчёта позиции каждой звезды
    const currentTime = Date.now()
    
    // Помечаем все звезды для исчезновения с сохранением текущей позиции
    setFallingStars(prev => prev.map(star => {
      // Вычисляем, сколько времени прошло с начала анимации
      const elapsedTime = (currentTime - (star.startTime || currentTime)) / 1000
      // Вычисляем текущую позицию (процент от всей анимации)
      const progress = Math.min(elapsedTime / star.duration, 1)
      // Вычисляем текущий top (от 0 до высоты окна)
      const currentTop = progress * window.innerHeight
      
      return {
        ...star, 
        fadeOut: true,
        frozenTop: currentTop // Сохраняем текущую позицию
      }
    }))
    // Удаляем все звезды через 2.5 секунды
    setTimeout(() => {
      setFallingStars([])
    }, 2500)
  }

  // --- EVENT HANDLERS ---
  
  // Use a ref to act as a stable proxy for event listeners, 
  // ensuring they always execute the LATEST updated logic without re-binding listeners.
  const handlersRef = useRef({})

  // Update handlers on every render so they close over the fresh state
  handlersRef.current = {
      handleMouseDown: (event) => {
        // Проверяем нажатие обеих кнопок
        const isDesktop = window.innerWidth >= 1024;
        if (isDesktop && event.buttons === 3) { // 3 = левая + правая кнопки
        event.preventDefault()
        if (!bothButtonsPressed) {
            setBothButtonsPressed(true)
            startFallingStars()
        }
        return
        }
        
        // Средняя кнопка (Колесико) - Черная дыра
        if (event.button === 1 && isDesktop) {
            event.preventDefault()
            setIsMiddleMouseHeld(true)
            setIsBlackHoleClosing(false)
            setBlackHolePos({ x: event.clientX, y: event.clientY })
            startBlackHoleSound()
            return
        }

        // Левая кнопка - Туманность (Nebula) - ОТКЛЮЧЕНО
        // if (event.button === 0 && isDesktop) {
        //    setIsLeftMouseHeld(true)
        // }
        
        if (event.button === 2) {
        // event.preventDefault() // We handle preventdefault later or in context menu
        
        // Показываем звёздочки и взрывы только на десктопе (больше 1024px)
        if (!isDesktop) {
            return; // На мобильных/планшетах не показываем эффекты
        }
        
        // Не создаём звёзды если активен режим падающих звёзд
        if (bothButtonsPressed) {
            return;
        }
        
        setHintClicked(true)
        setShowHint(false)
        setIsRightMouseHeld(true)
        
        const currentTime = Date.now()
        const timeSinceLastClick = currentTime - lastRightClickTime
        
        // Проверяем двойной клик (меньше 400ms между кликами)
        if (timeSinceLastClick < 400 && rightClickCount === 1) {
            setRightClickCount(2)
            setShowAdvancedHint(false)
            createExplosion(event.clientX, event.clientY)
        } else {
            setRightClickCount(1)
            // Убрали 5 звёзд при одиночном клике
            // Остался только след при движении
        }
        
        setLastRightClickTime(currentTime)
        
        // Сброс счётчика через 400ms
        setTimeout(() => {
            setRightClickCount(0)
        }, 400)
        }
    },
    handleMouseUp: (event) => {
        // Проверяем, отпущена ли хотя бы одна кнопка
        if (bothButtonsPressed && event.buttons !== 3) {
        setBothButtonsPressed(false)
        stopFallingStars()
        }
        
        // Отпускаем среднюю кнопку
        if (event.button === 1) {
            setIsMiddleMouseHeld(false)
            setIsBlackHoleClosing(true)
            stopBlackHoleSound()
        }

        // Отпускаем левую кнопку
        if (event.button === 0) {
            setIsLeftMouseHeld(false)
            // stopNebulaSound()
        }
        
        if (event.button === 2) {
            setIsRightMouseHeld(false)
        }
    },
    handleMouseMove: (event) => {
        // Проверяем, что обе кнопки всё ещё зажаты
        const isDesktop = window.innerWidth >= 1024;
        
        // Обновляем позицию черной дыры если зажато колесико
        if (isMiddleMouseHeld) {
            setBlackHolePos({ x: event.clientX, y: event.clientY })
        }

        // Создаем туманность если зажата левая кнопка
        if (isLeftMouseHeld && isDesktop) {
            const id = Date.now() + Math.random()
            setNebulaParticles(prev => [
                ...prev,
                { id, x: event.clientX, y: event.clientY, createdAt: Date.now() }
            ])
        }

        if (isDesktop && event.buttons === 3) {
            if (!bothButtonsPressed) {
                setBothButtonsPressed(true)
                startFallingStars()
            }
        } else if (bothButtonsPressed && event.buttons !== 3) { // Logic fix here
            setBothButtonsPressed(false)
            stopFallingStars()
        }
        
        // След из звёзд при движении с зажатой правой кнопкой (основные звёзды)
        if (event.buttons & 2) { // Right button is 2
        if (isDesktop) {
            createStar(event.clientX, event.clientY, false)
        }
        }
    }
  }

  useEffect(() => {
      // Attach "Proxy" listeners that delegate to the current handler in the ref
      const proxyDown = (e) => handlersRef.current.handleMouseDown(e)
      const proxyUp = (e) => handlersRef.current.handleMouseUp(e)
      const proxyMove = (e) => handlersRef.current.handleMouseMove(e)

      window.addEventListener('mousedown', proxyDown)
      window.addEventListener('mouseup', proxyUp)
      window.addEventListener('mousemove', proxyMove)
      
      return () => {
          window.removeEventListener('mousedown', proxyDown)
          window.removeEventListener('mouseup', proxyUp)
          window.removeEventListener('mousemove', proxyMove)
      }
  }, [])

  // Hints timer
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowHint(false)
    }, 5000)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (hintClicked && !showAdvancedHint && !advancedHintShown) {
      const timer = setTimeout(() => {
        setShowAdvancedHint(true)
        setAdvancedHintShown(true)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [hintClicked, showAdvancedHint, advancedHintShown])

  useEffect(() => {
    if (showAdvancedHint) {
      const timer = setTimeout(() => {
        setShowAdvancedHint(false)
      }, 6000)
      return () => clearTimeout(timer)
    }
  }, [showAdvancedHint])
  
  // Nebula cleanup
  useEffect(() => {
    if (nebulaParticles.length === 0) return
    const interval = setInterval(() => {
       const now = Date.now()
       setNebulaParticles(prev => {
           const next = prev.filter(p => now - p.createdAt < 5000)
           return next.length !== prev.length ? next : prev
       })
    }, 1000)
    return () => clearInterval(interval)
  }, [nebulaParticles.length])

  // --- ФИЗИКА ЧЕРНОЙ ДЫРЫ ---
  useEffect(() => {
    let animationFrame;
    if (isMiddleMouseHeld || isBlackHoleClosing) {
      const updateBlackHole = () => {
        // 1. Управление материей самой черной дыры (аккреционный диск)
        // Спавн новых частиц только если КНОПКА ЗАЖАТА (не закрываемся)
        if (isMiddleMouseHeld && Math.random() > 0.5) {
           const angle = Math.random() * Math.PI * 2
           const distance = 300 + Math.random() * 200 
           const x = blackHolePos.x + Math.cos(angle) * distance
           const y = blackHolePos.y + Math.sin(angle) * distance
           const id = Date.now() + Math.random()
           const color = Math.random() > 0.5 ? '#bf80ff' : '#ffffff' 
           
           setBlackHoleMatter(prev => [...prev, {
             id, x, y, 
             vx: (Math.random() - 0.5) * 2,
             vy: (Math.random() - 0.5) * 2,
             size: Math.random() * 3 + 1,
             color
           }])
        }

        // Обновление частиц материи
        setBlackHoleMatter(prev => prev.map(p => {
          const dx = blackHolePos.x - p.x
          const dy = blackHolePos.y - p.y
          const dist = Math.sqrt(dx*dx + dy*dy)
          
          if (dist < 20) return null // Поглощена горизонтом событий

          // При закрытии гравитация сильнее, чтобы быстро засосать остатки
          const gravityMultiplier = isBlackHoleClosing ? 2.5 : 1
          const force = (2000 / (dist * dist + 100)) * gravityMultiplier
          
          const ax = (dx / dist) * force
          const ay = (dy / dist) * force
          const spiral = 0.08
          
          return {
            ...p,
            vx: (p.vx + ax) * 0.96 + (dy/dist) * spiral * force * 15, 
            vy: (p.vy + ay) * 0.96 - (dx/dist) * spiral * force * 15,
            x: p.x + p.vx,
            y: p.y + p.vy
          }
        }).filter(Boolean))

        // --- ВЗАИМОДЕЙСТВИЕ С ОКРУЖАЮЩИМ МИРОМ ---
        const G = isBlackHoleClosing ? 8000 : 5000; // Усиливаем гравитацию при закрытии
        const HORIZON = 50; 

        // 2. Поглощение обычных звезд
        setStars(prevStars => prevStars.map(s => {
            const dx = blackHolePos.x - s.x;
            const dy = blackHolePos.y - s.y;
            const distSq = dx*dx + dy*dy;
            
            // Если звезда слишком близко - она исчезает (съедена)
            if (distSq < HORIZON * HORIZON) {
                return { ...s, fadeOut: true, duration: 0.1 }; 
            }
            
            // Если звезда в радиусе действия гравитации
            if (distSq < 360000) { 
                const dist = Math.sqrt(distSq);
                const force = G / (distSq + 1000);
                
                return { 
                    ...s, 
                    x: s.x + (dx/dist) * force * 5, 
                    y: s.y + (dy/dist) * force * 5 
                };
            }
            return s;
        }).filter(s => !(s.fadeOut && s.duration <= 0.2))); 

        // 3. Искажение туманности (Nebula)
        setNebulaParticles(prevNebula => prevNebula.map(p => {
            const dx = blackHolePos.x - p.x;
            const dy = blackHolePos.y - p.y;
            const distSq = dx*dx + dy*dy;
            
            if (distSq < HORIZON * HORIZON) return null; // Газ поглощен
            
            if (distSq < 400000) {
                 const dist = Math.sqrt(distSq);
                 const force = G / (distSq + 2000);
                 // Газ закручивается сильнее
                 return { 
                     ...p, 
                     x: p.x + (dx/dist) * force * 8 + (dy/dist) * force * 2, // +сдвиг по спирали
                     y: p.y + (dy/dist) * force * 8 - (dx/dist) * force * 2
                 }
            }
            return p;
        }).filter(Boolean));
        
        // 4. Искривление траектории падающих звезд
        setFallingStars(prev => prev.map(s => {
             const dx = blackHolePos.x - s.x;
             const dist = Math.sqrt(dx*dx + 10000); 

             if (dist < 500) {
                 const force = (G / (dist * dist)) * Math.sign(dx) * 50; 
                 return { ...s, x: s.x + force }
             }
             return s;
        }));
        
        animationFrame = requestAnimationFrame(updateBlackHole)
      }
      animationFrame = requestAnimationFrame(updateBlackHole)
    } else {
      // Очищаем материю только когда полностью закрылись
      setBlackHoleMatter([])
    }
    return () => cancelAnimationFrame(animationFrame)
  }, [isMiddleMouseHeld, blackHolePos, isBlackHoleClosing])

  // Таймер управления закрытием черной дыры
  useEffect(() => {
    if (isBlackHoleClosing) {
      // Даем 2 секунды на засасывание остатков и анимацию уменьшения
      const timer = setTimeout(() => {
        setIsBlackHoleClosing(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [isBlackHoleClosing])


  return (
    <>
      <style>
        {`
          @keyframes starFloat {
            0% {
              transform: translate(0, 0) scale(0) rotate(var(--start-rotation));
              opacity: 0;
            }
            10% {
              transform: translate(0, 0) scale(1.2) rotate(calc(var(--start-rotation) + 60deg));
              opacity: 1;
            }
            60% {
              transform: translate(var(--tx), var(--ty)) scale(1.2) rotate(calc(var(--start-rotation) + 300deg));
              opacity: 1;
            }
            100% {
              transform: translate(var(--tx), var(--ty)) scale(0) rotate(calc(var(--start-rotation) + 360deg));
              opacity: 0;
            }
          }
          ... existing animations ...
          /* To save space, I will trust that the animation keyframes are correct as I pasted them. */
          @keyframes starFloatFadeOut {
            0% {
              opacity: 1;
              transform: translate(var(--tx), var(--ty)) scale(1.2) rotate(calc(var(--start-rotation) + 300deg));
            }
            100% {
              opacity: 0;
              transform: translate(var(--tx), var(--ty)) scale(0) rotate(calc(var(--start-rotation) + 360deg));
            }
          }
          
          @keyframes hintFadeIn {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes hintPulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
          }
          
          @keyframes cursorBlink {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.3;
            }
          }
          
          @keyframes explosionRing {
            0% {
              transform: scale(0);
              opacity: 1;
            }
            50% {
              opacity: 0.8;
            }
            100% {
              transform: scale(3);
              opacity: 0;
            }
          }
          
          @keyframes explosionShockwave {
            0% {
              transform: scale(0);
              opacity: 0.8;
            }
            100% {
              transform: scale(5);
              opacity: 0;
            }
          }
          
          @keyframes starFall {
            0% {
              transform: translateY(0) rotate(var(--start-rotation));
              opacity: 1;
            }
            95% {
              opacity: 1;
            }
            100% {
              transform: translateY(100vh) rotate(calc(var(--start-rotation) + 180deg));
              opacity: 0;
            }
          }
          
          @keyframes starFadeOut {
            0% {
              opacity: 1;
            }
            100% {
              opacity: 0;
            }
          }
          

          @keyframes starFadeOutInPlace {
            0% {
              opacity: 1;
            }
            100% {
              opacity: 0;
            }
          }

          @keyframes nebulaPulse {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 0; }
            15% { opacity: 0.6; }
            100% { transform: translate(-50%, -50%) scale(2.5); opacity: 0; }
          }
          
          @keyframes lineFade {
            0% { opacity: 1; }
            100% { opacity: 0; }
          }
          
           @keyframes blackHoleSpin {
            from { transform: translate(-50%, -50%) rotate(0deg); }
            to { transform: translate(-50%, -50%) rotate(360deg); }
          }

          @keyframes blackHoleCollapse {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            50% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            100% { transform: translate(-50%, -50%) scale(0); opacity: 0; }
          }
        `}
      </style>
      
      {/* SVG ФИЛЬТРЫ ДЛЯ ТУМАННОСТИ И ЧЕРНОЙ ДЫРЫ */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <filter id="nebulaFilter">
            <feTurbulence type="fractalNoise" baseFrequency="0.015" numOctaves="3" result="warp" />
            <feDisplacementMap xChannelSelector="R" yChannelSelector="G" scale="30" in="SourceGraphic" in2="warp" />
            <feGaussianBlur stdDeviation="4" />
          </filter>
        </defs>
      </svg>
      
      {/* ЧЕРНАЯ ДЫРА */}
      {(isMiddleMouseHeld || isBlackHoleClosing) && (
          <div style={{
              position: 'fixed',
              left: blackHolePos.x,
              top: blackHolePos.y,
              zIndex: 99997,
              pointerEvents: 'none',
              animation: isBlackHoleClosing ? 'blackHoleCollapse 2s ease-in forwards' : undefined
          }}>
             {/* Аккреционный диск */}
             <div style={{
                 position: 'absolute',
                 width: '200px',
                 height: '200px',
                 background: 'radial-gradient(circle, transparent 30%, rgba(200, 100, 255, 0.8) 40%, transparent 70%)',
                 borderRadius: '50%',
                 transform: 'translate(-50%, -50%)',
                 // Вращение не прерываем, так как на родителе scale схлопнется
                 animation: 'blackHoleSpin 0.5s linear infinite', 
                 border: '2px dashed rgba(255, 255, 255, 0.3)'
             }} />
              {/* Событийный горизонт */}
             <div style={{
                 position: 'absolute',
                 width: '60px',
                 height: '60px',
                 background: '#000',
                 borderRadius: '50%',
                 transform: 'translate(-50%, -50%)',
                 boxShadow: '0 0 20px rgba(100, 50, 200, 0.8), inset 0 0 20px rgba(255, 255, 255, 0.5)'
             }} />
          </div>
      )}
      
      {/* МАТЕРИЯ ЧЕРНОЙ ДЫРЫ (Засасываемые звезды) */}
      {blackHoleMatter.map(p => (
        <div key={p.id} style={{
            position: 'fixed',
            left: p.x, 
            top: p.y,
            width: p.size,
            height: p.size,
            background: p.color,
            borderRadius: '50%',
            pointerEvents: 'none',
            zIndex: 99997,
            boxShadow: `0 0 ${p.size*2}px ${p.color}`
        }} />
      ))}

      {/* ТУМАННОСТЬ (NEBULA) + СОЗВЕЗДИЯ (СВЯЗИ) */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 99990 }}>
          {/* Линии созвездий между частицами туманности */}
          <svg style={{ width: '100%', height: '100%' }}>
            {nebulaParticles.map((p1, i) => (
               nebulaParticles.slice(i + 1).map((p2, j) => {
                   const dx = p1.x - p2.x
                   const dy = p1.y - p2.y
                   const dist = Math.sqrt(dx*dx + dy*dy)
                   if (dist < 100) {
                        return (
                           <line 
                             key={`${p1.id}-${p2.id}`}
                             x1={p1.x} y1={p1.y}
                             x2={p2.x} y2={p2.y}
                             stroke="rgba(200, 200, 255, 0.3)"
                             strokeWidth={1 - dist/100}
                             style={{ animation: 'lineFade 5s ease-out forwards' }}
                           />
                        )
                   }
                   return null
               })
            ))}
          </svg>
          
          {/* Сами частицы туманности */}
          {nebulaParticles.map(p => (
              <div key={p.id} style={{
                  position: 'absolute',
                  left: p.x,
                  top: p.y,
                  width: '60px',
                  height: '60px',
                  background: 'radial-gradient(circle, rgba(100, 200, 255, 0.4) 0%, transparent 70%)',
                  borderRadius: '50%',
                  transform: 'translate(-50%, -50%)',
                  animation: 'nebulaPulse 5s ease-out forwards',
                  filter: 'url(#nebulaFilter)' // Применяем искажение для эффекта газа
              }} />
          ))}
      </div>
      
      {showHint && !hintClicked && window.innerWidth >= 1024 && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'linear-gradient(135deg, rgba(30, 30, 40, 0.95), rgba(50, 40, 80, 0.95))',
            backdropFilter: 'blur(10px)',
            padding: '24px 32px',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 60px rgba(150, 100, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            zIndex: 10000,
            animation: 'hintFadeIn 0.6s ease-out, hintPulse 2s ease-in-out infinite',
            border: '1px solid rgba(150, 100, 255, 0.3)',
            textAlign: 'center',
            maxWidth: '400px'
          }}
        >
          <div style={{
            marginBottom: '16px',
            animation: 'cursorBlink 1.5s ease-in-out infinite'
          }}>
            <svg width="64" height="64" viewBox="0 0 64 64" style={{ filter: 'drop-shadow(0 0 20px rgba(255, 200, 255, 0.9)) drop-shadow(0 0 40px rgba(200, 150, 255, 0.6))' }}>
              <defs>
                <radialGradient id="starCoreGrad" cx="50%" cy="50%">
                  <stop offset="0%" stopColor="rgba(255, 255, 255, 1)" />
                  <stop offset="20%" stopColor="rgba(255, 240, 255, 1)" />
                  <stop offset="50%" stopColor="rgba(255, 200, 255, 0.95)" />
                  <stop offset="80%" stopColor="rgba(230, 180, 255, 0.8)" />
                  <stop offset="100%" stopColor="rgba(200, 150, 255, 0.6)" />
                </radialGradient>
                <radialGradient id="starAuraGrad" cx="50%" cy="50%">
                  <stop offset="0%" stopColor="rgba(255, 220, 255, 0.4)" />
                  <stop offset="50%" stopColor="rgba(230, 180, 255, 0.2)" />
                  <stop offset="100%" stopColor="rgba(200, 150, 255, 0)" />
                </radialGradient>
                <linearGradient id="rayGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(255, 255, 255, 0)" />
                  <stop offset="30%" stopColor="rgba(255, 230, 255, 0.6)" />
                  <stop offset="50%" stopColor="rgba(255, 210, 255, 0.9)" />
                  <stop offset="70%" stopColor="rgba(255, 230, 255, 0.6)" />
                  <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
                </linearGradient>
              </defs>
              <circle cx="32" cy="32" r="30" fill="url(#starAuraGrad)" opacity="0.6"><animate attributeName="r" values="28;32;28" dur="3s" repeatCount="indefinite" /><animate attributeName="opacity" values="0.4;0.7;0.4" dur="3s" repeatCount="indefinite" /></circle>
              <circle cx="32" cy="32" r="20" fill="url(#starAuraGrad)" opacity="0.8"><animate attributeName="r" values="18;22;18" dur="2.5s" repeatCount="indefinite" /><animate attributeName="opacity" values="0.6;0.9;0.6" dur="2.5s" repeatCount="indefinite" /></circle>
              <ellipse cx="32" cy="32" rx="28" ry="1.5" fill="url(#rayGrad)" opacity="0.8"><animate attributeName="opacity" values="0.6;1;0.6" dur="2s" repeatCount="indefinite" /></ellipse>
              <ellipse cx="32" cy="32" rx="1.5" ry="28" fill="url(#rayGrad)" opacity="0.8"><animate attributeName="opacity" values="0.6;1;0.6" dur="2s" begin="0.5s" repeatCount="indefinite" /></ellipse>
              <ellipse cx="32" cy="32" rx="28" ry="1.2" fill="url(#rayGrad)" opacity="0.7" transform="rotate(45 32 32)"><animate attributeName="opacity" values="0.5;0.9;0.5" dur="2s" begin="0.25s" repeatCount="indefinite" /></ellipse>
              <ellipse cx="32" cy="32" rx="28" ry="1.2" fill="url(#rayGrad)" opacity="0.7" transform="rotate(-45 32 32)"><animate attributeName="opacity" values="0.5;0.9;0.5" dur="2s" begin="0.75s" repeatCount="indefinite" /></ellipse>
              <circle cx="32" cy="32" r="8" fill="url(#starCoreGrad)"><animate attributeName="r" values="7;9;7" dur="2s" repeatCount="indefinite" /></circle>
              <circle cx="32" cy="32" r="4" fill="rgba(255, 255, 255, 1)"><animate attributeName="r" values="3.5;4.5;3.5" dur="2s" repeatCount="indefinite" /><animate attributeName="opacity" values="0.9;1;0.9" dur="2s" repeatCount="indefinite" /></circle>
              <circle cx="32" cy="32" r="2" fill="rgba(255, 255, 255, 1)" opacity="0.95"><animate attributeName="r" values="1.5;2.5;1.5" dur="1.5s" repeatCount="indefinite" /></circle>
            </svg>
          </div>
          <div style={{
            color: '#fff',
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '12px',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)'
          }}>
            Попробуй магию звёзд!
          </div>
       
          <svg width="120" height="100" viewBox="0 0 120 100" style={{ filter: 'drop-shadow(0 4px 12px rgba(150, 100, 255, 0.5))' }}>
            <g transform="translate(30, 20)"><path d="M 0 0 L 0 24 L 7 18 L 11 26 L 14 25 L 10 17 L 18 17 Z" fill="white" stroke="rgba(150, 100, 255, 0.8)" strokeWidth="1"/></g>
            <g transform="translate(55, 30)">
              <rect x="0" y="10" width="35" height="50" rx="17" fill="url(#mouseGradient)" stroke="rgba(150, 100, 255, 0.6)" strokeWidth="2"/>
              <line x1="17.5" y1="10" x2="17.5" y2="35" stroke="rgba(150, 100, 255, 0.4)" strokeWidth="1.5"/>
              <path d="M 17.5 10 Q 35 10 35 27.5 L 35 10 Z" fill="rgba(150, 100, 255, 0.6)" stroke="none"><animate attributeName="opacity" values="0.6;1;0.6" dur="1.5s" repeatCount="indefinite"/></path>
              <rect x="14" y="20" width="7" height="10" rx="3" fill="rgba(150, 100, 255, 0.3)" stroke="rgba(150, 100, 255, 0.5)" strokeWidth="1"/>
            </g>
            <g transform="translate(85, 45)">
              <path d="M 0 0 L 8 0 L 8 -4 L 15 4 L 8 12 L 8 8 L 0 8 Z" fill="rgba(150, 100, 255, 0.8)">
                <animate attributeName="opacity" values="0.8;1;0.8" dur="1s" repeatCount="indefinite"/>
                <animateTransform attributeName="transform" type="translate" values="0,0; 3,0; 0,0" dur="1.5s" repeatCount="indefinite"/>
              </path>
            </g>
            <defs>
              <linearGradient id="mouseGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(60, 60, 80, 0.9)" />
                <stop offset="100%" stopColor="rgba(40, 40, 60, 0.9)" />
              </linearGradient>
            </defs>
            <text x="60" y="92" textAnchor="middle" fill="rgba(255, 255, 255, 0.7)" fontSize="11" fontWeight="600" style={{ fontFamily: 'Arial, sans-serif' }}>
              Правая кнопка
            </text>
          </svg>
        </div>
      )}
      
      {showAdvancedHint && window.innerWidth >= 1024 && (
        <div
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            background: 'linear-gradient(135deg, rgba(80, 40, 120, 0.95), rgba(120, 60, 180, 0.95))',
            backdropFilter: 'blur(10px)',
            padding: '16px 24px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(150, 100, 255, 0.4), 0 0 40px rgba(150, 100, 255, 0.2)',
            zIndex: 10000,
            animation: 'hintFadeIn 0.5s ease-out',
            border: '2px solid rgba(200, 150, 255, 0.5)',
            maxWidth: '320px'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '8px'
          }}>
            <svg width="28" height="28" viewBox="0 0 28 28" style={{ filter: 'drop-shadow(0 2px 6px rgba(255, 150, 255, 0.6))' }}>
              <defs>
                <radialGradient id="fireworkGrad" cx="50%" cy="50%">
                  <stop offset="0%" stopColor="rgba(255, 200, 255, 1)" />
                  <stop offset="50%" stopColor="rgba(200, 150, 255, 0.8)" />
                  <stop offset="100%" stopColor="rgba(150, 100, 255, 0.6)" />
                </radialGradient>
              </defs>
              <g>
                <circle cx="14" cy="14" r="3" fill="url(#fireworkGrad)"><animate attributeName="opacity" values="1;0.5;1" dur="1.5s" repeatCount="indefinite" /></circle>
                <path d="M14 4 L14 10" stroke="rgba(255, 150, 255, 0.9)" strokeWidth="2" strokeLinecap="round"/>
                <path d="M14 18 L14 24" stroke="rgba(255, 150, 255, 0.9)" strokeWidth="2" strokeLinecap="round"/>
                <path d="M4 14 L10 14" stroke="rgba(255, 150, 255, 0.9)" strokeWidth="2" strokeLinecap="round"/>
                <path d="M18 14 L24 14" stroke="rgba(255, 150, 255, 0.9)" strokeWidth="2" strokeLinecap="round"/>
                <path d="M8 8 L11 11" stroke="rgba(200, 150, 255, 0.8)" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M17 17 L20 20" stroke="rgba(200, 150, 255, 0.8)" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M20 8 L17 11" stroke="rgba(200, 150, 255, 0.8)" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M11 17 L8 20" stroke="rgba(200, 150, 255, 0.8)" strokeWidth="1.5" strokeLinecap="round"/>
              </g>
            </svg>
            <div style={{
              color: '#fff',
              fontSize: '16px',
              fontWeight: '600',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)'
            }}>
              Открой магию звёздных взрывов!
            </div>
          </div>
          <div style={{
            color: 'rgba(255, 255, 255, 0.85)',
            fontSize: '13px',
            lineHeight: '1.5',
            marginLeft: '40px'
          }}>
            <strong>Двойной клик</strong> правой кнопкой = <svg width="16" height="16" viewBox="0 0 16 16" style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: '4px', filter: 'drop-shadow(0 1px 4px rgba(200, 150, 255, 0.6))' }}>
              <path d="M8 2 L9 6 L13 6 L10 9 L11 13 L8 10 L5 13 L6 9 L3 6 L7 6 Z" fill="rgba(255, 200, 255, 0.9)" stroke="rgba(200, 150, 255, 0.7)" strokeWidth="0.5">
                <animateTransform attributeName="transform" type="rotate" from="0 8 8" to="360 8 8" dur="3s" repeatCount="indefinite" />
              </path>
            </svg> Космическая магия
          </div>
        </div>
      )}


      {/* Кнопка управления звуком removed (moved to parent) */}
      
        {/* Падающие звёзды */}
        {fallingStars.map(star => {
          const createStarPath = (points, outerRadius, innerRadius) => {
            let path = ''
            for (let i = 0; i < points * 2; i++) {
              const radius = i % 2 === 0 ? outerRadius : innerRadius
              const angle = (Math.PI * i) / points
              const x = 50 + radius * Math.sin(angle)
              const y = 50 - radius * Math.cos(angle)
              path += `${i === 0 ? 'M' : 'L'}${x},${y} `
            }
            return path + 'Z'
          }
          
          return (
            <div
              key={star.id}
              style={{
                position: 'fixed',
                left: `${star.x}px`,
                top: '0',
                pointerEvents: 'none',
                zIndex: 99998,
                '--start-rotation': `${star.rotation}deg`,
                animation: star.fadeOut 
                  ? `starFall ${star.duration}s linear forwards, starFadeOutInPlace 2.5s ease-in-out forwards`
                  : `starFall ${star.duration}s linear ${star.delay}s forwards`
              }}
            >
              <svg
                width={star.size * 8}
                height={star.size * 8}
                viewBox="0 0 100 100"
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  filter: `drop-shadow(0 0 ${star.size * 2}px rgba(255, 255, 255, 0.9)) drop-shadow(0 0 ${star.size * 3}px rgba(200, 150, 255, 0.7)) drop-shadow(0 0 ${star.size * 4}px rgba(150, 100, 255, 0.5))`
                }}
              >
                <defs>
                  <radialGradient id={`falling-grad-${star.id}`} cx="30%" cy="30%">
                    <stop offset="0%" stopColor="rgba(255, 255, 255, 1)" />
                    <stop offset="30%" stopColor="rgba(230, 200, 255, 0.95)" />
                    <stop offset="60%" stopColor="rgba(200, 160, 255, 0.85)" />
                    <stop offset="100%" stopColor="rgba(160, 120, 255, 0.7)" />
                  </radialGradient>
                </defs>
                <path
                  d={createStarPath(star.points, 45, 20)}
                  fill={`url(#falling-grad-${star.id})`}
                  stroke="rgba(255, 255, 255, 0.8)"
                  strokeWidth="1"
                />
              </svg>
            </div>
          )
        })}
        
        {explosions.map(explosion => (
          <div key={explosion.id} style={{ position: 'fixed', left: 0, top: 0, pointerEvents: 'none', zIndex: 99999 }}>
            {/* Основное кольцо взрыва */}
            <div
              style={{
                position: 'absolute',
                left: `${explosion.x}px`,
                top: `${explosion.y}px`,
                width: '200px',
                height: '200px',
                marginLeft: '-100px',
                marginTop: '-100px',
                borderRadius: '50%',
                border: '4px solid rgba(255, 200, 255, 0.8)',
                boxShadow: '0 0 40px rgba(255, 150, 255, 0.8), inset 0 0 40px rgba(255, 200, 255, 0.6)',
                animation: 'explosionRing 1s ease-out',
                zIndex: 99999
              }}
            />
            
            {/* Ударная волна 1 */}
            <div
              style={{
                position: 'absolute',
                left: `${explosion.x}px`,
                top: `${explosion.y}px`,
                width: '150px',
                height: '150px',
                marginLeft: '-75px',
                marginTop: '-75px',
                borderRadius: '50%',
                border: '3px solid rgba(200, 150, 255, 0.6)',
                animation: 'explosionShockwave 1.2s ease-out',
                zIndex: 99999
              }}
            />
            
            {/* Ударная волна 2 */}
            <div
              style={{
                position: 'absolute',
                left: `${explosion.x}px`,
                top: `${explosion.y}px`,
                width: '150px',
                height: '150px',
                marginLeft: '-75px',
                marginTop: '-75px',
                borderRadius: '50%',
                border: '2px solid rgba(150, 100, 255, 0.4)',
                animation: 'explosionShockwave 1.5s ease-out 0.1s',
                zIndex: 99999
              }}
            />
            
            {/* Центральная вспышка */}
            <div
              style={{
                position: 'absolute',
                left: `${explosion.x}px`,
                top: `${explosion.y}px`,
                width: '60px',
                height: '60px',
                marginLeft: '-30px',
                marginTop: '-30px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255, 255, 255, 1) 0%, rgba(255, 200, 255, 0.8) 30%, rgba(200, 150, 255, 0) 70%)',
                boxShadow: '0 0 60px rgba(255, 200, 255, 1), 0 0 100px rgba(200, 150, 255, 0.6)',
                animation: 'explosionRing 0.8s ease-out',
                zIndex: 99999
              }}
            />
          </div>
        ))}
        
        {stars.map(star => {
          const radAngle = (star.angle * Math.PI) / 180
          const tx = Math.cos(radAngle) * star.distance
          const ty = Math.sin(radAngle) * star.distance
          
          const createStarPath = (points, outerRadius, innerRadius) => {
            let path = ''
            for (let i = 0; i < points * 2; i++) {
              const radius = i % 2 === 0 ? outerRadius : innerRadius
              const angle = (Math.PI * i) / points
              const x = 50 + radius * Math.sin(angle)
              const y = 50 - radius * Math.cos(angle)
              path += `${i === 0 ? 'M' : 'L'}${x},${y} `
            }
            return path + 'Z'
          }
          
          return (
            <div
              key={star.id}
              style={{
                position: 'fixed',
                left: `${star.x}px`,
                top: `${star.y}px`,
                width: '0',
                height: '0',
                pointerEvents: 'none',
                zIndex: 99999,
                '--tx': `${tx}px`,
                '--ty': `${ty}px`,
                '--start-rotation': `${star.rotation}deg`,
                animation: `starFloat ${star.duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards`
              }}
            >
              <svg
                width={star.size * 8}
                height={star.size * 8}
                viewBox="0 0 100 100"
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  filter: star.isMagic 
                    ? `drop-shadow(0 0 ${star.size * 3}px rgba(255, 255, 255, 1)) drop-shadow(0 0 ${star.size * 5}px rgba(255, 150, 255, 0.9)) drop-shadow(0 0 ${star.size * 7}px rgba(200, 100, 255, 0.7))`
                    : `drop-shadow(0 0 ${star.size * 2}px rgba(255, 255, 255, 0.9)) drop-shadow(0 0 ${star.size * 3}px rgba(200, 150, 255, 0.7)) drop-shadow(0 0 ${star.size * 4}px rgba(150, 100, 255, 0.5))`
                }}
              >
                <defs>
                  <radialGradient id={`grad-${star.id}`} cx="30%" cy="30%">
                    {star.isMagic ? (
                      <>
                        <stop offset="0%" stopColor="rgba(255, 255, 255, 1)" />
                        <stop offset="20%" stopColor="rgba(255, 200, 255, 1)" />
                        <stop offset="50%" stopColor="rgba(255, 150, 255, 0.95)" />
                        <stop offset="80%" stopColor="rgba(200, 100, 255, 0.85)" />
                        <stop offset="100%" stopColor="rgba(150, 80, 255, 0.7)" />
                      </>
                    ) : (
                      <>
                        <stop offset="0%" stopColor="rgba(255, 255, 255, 1)" />
                        <stop offset="30%" stopColor="rgba(230, 200, 255, 0.95)" />
                        <stop offset="60%" stopColor="rgba(200, 160, 255, 0.85)" />
                        <stop offset="100%" stopColor="rgba(160, 120, 255, 0.7)" />
                      </>
                    )}
                  </radialGradient>
                </defs>
                <path
                  d={createStarPath(star.points, 45, 20)}
                  fill={`url(#grad-${star.id})`}
                  stroke="rgba(255, 255, 255, 0.8)"
                  strokeWidth="1"
                />
              </svg>
            </div>
          )
        })}
    </>
  )
}

export default CosmicMode
